import { motion, useReducedMotion } from "framer-motion";
import { GoldButton } from "@/components/primitives";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import GrowthChart from "@/components/GrowthChart";
import { capabilities } from "@/data/content";
import { site } from "@/config/site";

const HEAD_LINES = ["A premium consulting firm", "for businesses ready to", "operate better."];
const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay, className = "" }) {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={reduce ? { opacity: 0 } : { opacity: 0, y: "108%" }}
        animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
        transition={{ duration: 0.85, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-fs-bg pb-16 pt-32 lg:pb-24 lg:pt-40"
    >
      <div className="fs-grid-bg pointer-events-none absolute inset-0 opacity-40" aria-hidden="true" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 top-24 h-80 w-80 rounded-full bg-fs-gold/[0.05] blur-3xl"
      />

      <div className="fs-shell relative">
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
          {/* Left */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="fs-label"
              data-testid="hero-label"
            >
              <span className="h-px w-8 bg-fs-gold/70" />
              Strategy · Systems · Scale
            </motion.div>

            <h1 className="mt-7 fs-h1" data-testid="hero-headline">
              {HEAD_LINES.map((line, i) => (
                <MaskedLine key={i} delay={0.15 + i * 0.12}>
                  {line}
                </MaskedLine>
              ))}
              <MaskedLine delay={0.6} className="fs-italic-gold mt-2 text-3xl sm:text-4xl md:text-5xl">
                Strategy. Systems. Scale.
              </MaskedLine>
            </h1>

            <motion.p
              className="mt-8 max-w-xl fs-body"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              data-testid="hero-copy"
            >
              We help service businesses bring clarity to their offer, build
              high-converting digital infrastructure, automate follow-up, and
              implement systems that remove friction and drive consistent growth.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-col gap-4 sm:flex-row"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.95 }}
            >
              <GoldButton to="/services" variant="solid" testId="hero-explore-services">
                Explore Services
              </GoldButton>
              <GoldButton href={site.bookingUrl} target="_blank" rel="noopener noreferrer" variant="ghost" testId="hero-book-call">
                Book a Strategy Call
              </GoldButton>
            </motion.div>
          </div>

          {/* Right — abstract growth visual */}
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.7, ease: EASE }}
          >
            <div className="border border-fs-border bg-fs-card/60 p-6 backdrop-blur-sm lg:p-8">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.24em] text-fs-muted/70">
                  Operating System
                </span>
                <span className="h-1.5 w-1.5 rounded-full bg-fs-gold" />
              </div>
              <GrowthChart />
            </div>
          </motion.div>
        </div>

        {/* Capabilities strip */}
        <StaggerGroup className="mt-20 grid gap-px border border-fs-border bg-fs-border sm:grid-cols-2 lg:grid-cols-4">
          {capabilities.map((c) => {
            const Icon = c.icon;
            return (
              <StaggerItem key={c.title}>
                <div
                  className="group h-full bg-fs-bg p-7 transition-colors duration-300 hover:bg-fs-card"
                  data-testid={`capability-${c.title.toLowerCase()}`}
                >
                  <Icon className="h-5 w-5 text-fs-gold" strokeWidth={1.4} />
                  <h3 className="mt-5 font-serif text-xl text-fs-ink">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-fs-muted">{c.desc}</p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
}
